import Toast from "react-native-toast-message";
import * as React from "react";
import { StyleSheet, TouchableOpacity } from "react-native";
import { View, Text } from "../Themed";
import Colors from "../../constants/Colors";
import OpenHouseListItem from "../OpenHouseListItem";
import { useNavigation } from "@react-navigation/native";
import GlobalContext from "../../context/Global";
import { calculateHoursBetween } from "../../utils/CalculateHoursBetween";
import useSyncOpenHouses from "../../hooks/useSyncOpenHouses";
import { getLocalData } from "../../services/storage";

type Props = {
  openHousesData: any;
  status: string;
  getLocalCompleted?: () => void;
};

const OpenHouseList = React.forwardRef((props: Props, ref) => {
  const { openHousesData, status, getLocalCompleted } = props;
  const { navigate } = useNavigation();
  const { user, isTablet } = React.useContext(GlobalContext);

  const openHouses =
    openHousesData?.pages?.reduce((acc, page) => {
      return acc?.concat(page?.openHouses || []);
    }, []) || [];

  const syncOpenHouses = useSyncOpenHouses();
  React.useImperativeHandle(ref, () => ({
    syncAllOpenHouses: async () => {
      await syncOpenHouses();
      getLocalCompleted?.();
    },
  }));

  const syncingIds = React.useRef(new Set<string>());
  const [busyIds, setBusyIds] = React.useState(new Set<string>());
  const syncHouse = async (uuid: string) => {
    if (!uuid || syncingIds.current.has(uuid)) return;
    syncingIds.current.add(uuid);
    setBusyIds(new Set(syncingIds.current));
    try {
      const result = await syncOpenHouses(uuid);
      await getLocalCompleted?.();
      Toast.show({ type: result.failed ? "error" : "success",
        text1: result.failed ? "Some changes could not sync" : "Sync complete",
        text2: result.failed ? "Your pending changes are saved. Please try again." : undefined });
    } catch {
      Toast.show({ type: "error", text1: "Unable to sync", text2: "Your pending changes are saved. Please try again." });
    } finally {
      syncingIds.current.delete(uuid);
      setBusyIds(new Set(syncingIds.current));
    }
  };

  const [filteredOpenHouses, setFilteredOpenHouses] = React.useState([]);

  React.useEffect(() => {
    const fetchFilteredHouses = async () => {
      const syncData = await getLocalData(`syncData`);
      if (status === "upcoming" && syncData?.openHouse?.length > 0) {
        const filtered = openHouses?.filter((item: any) => {
          return !syncData?.some(
            (syncItem: any) => syncItem.openHouse.uuid === item.uuid
          );
        });
        setFilteredOpenHouses(filtered || []);
        return;
      } else if (status === "active" && syncData?.length > 0) {
        const filtered = openHouses?.filter((item: any) => {
          return !syncData?.some(
            (syncItem: any) => syncItem?.data?.uuid === item.uuid
          );
        });
        setFilteredOpenHouses(filtered || []);
        return;
      } else if (status === "cached") {
        const unwrappedData = openHousesData?.pages[0]?.map(
          (item: any) => item.openHouse
        );
        setFilteredOpenHouses(unwrappedData || []);
        return;
      } else {
        setFilteredOpenHouses(openHouses || []);
        return;
      }
    };

    fetchFilteredHouses();
  }, [openHousesData, status]);

  const openHouseListItemPressed = (item: Object) => {
    navigate("OpenHomeDetails", { openHouse: item });
  };

  const getTitle = () => {
    switch (status) {
      case "upcoming":
        return "Upcoming";
      case "active":
        return "Current";
      case "cached":
        return "Sync Required";
      case "completed":
        return "Completed";
    }
  };

  return (
    (openHouses || openHousesData?.pages[0]) && (
      <View
        style={[
          styles.container,
          {
            marginTop: status === "current" ? 0 : 24,
          },
        ]}
      >
        <View style={styles.section}>
          <View
            style={[
              styles.sectionHeader,
              {
                paddingHorizontal: isTablet ? 32 : 16,
              },
            ]}
          >
            <Text
              size={20}
              lineHeight={28}
              lightColor={Colors.nuetral_grey_700}
            >
              {getTitle()}
            </Text>
            {filteredOpenHouses?.length > 4 && (
              <Text
                size={16}
                lineHeight={24}
                lightColor={Colors.primary_500}
                onPress={() =>
                  navigate("ViewAllOpenHouses", {
                    openHouseParams: { status, uuid: user?.openHouseUuid },
                    status,
                    openHouses: filteredOpenHouses,
                  })
                }
              >
                View All
              </Text>
            )}
          </View>
          {filteredOpenHouses?.length === 0 && (
            <View
              style={{
                flexDirection: "row",
                gap: 24,
                paddingVertical: 16,
                paddingHorizontal: isTablet ? 32 : 16,
              }}
              lightColor={Colors.base_colors_white}
              darkColor={Colors.nuetral_grey_700}
            >
              <Text lightColor={Colors.nuetral_grey_700}>
                No {getTitle()?.toLocaleLowerCase()} open houses
              </Text>
            </View>
          )}
          {filteredOpenHouses?.slice(0, 4).map((item: any, index: number) => {
            return status === "cached" ? (
              <OpenHouseListItem
                key={index}
                address={item?.propertyDetails}
                date={item?.start_date}
                attendees={item?.manual_attendee_count || 0}
                hours={
                  item?.completed_at
                    ? calculateHoursBetween(item?.start_time, item?.end_time)
                    : 0
                }
                startTime={item?.start_time}
                endTime={item?.end_time}
                onPress={() => openHouseListItemPressed(item)}
                syncRequired={true}
                sync={() => void syncHouse(item.uuid)}
                syncing={busyIds.has(item.uuid)}
                item={item}
              />
            ) : (
              <OpenHouseListItem
                key={index}
                address={item?.propertyDetails}
                date={item?.start_date}
                completed={status === "completed"}
                attendees={item?.manual_attendee_count || 0}
                hours={
                  item?.completed_at
                    ? calculateHoursBetween(item?.start_time, item?.end_time)
                    : 0
                }
                startTime={item?.start_time}
                endTime={item?.end_time}
                onPress={() => openHouseListItemPressed(item)}
                item={item}
              />
            );
          })}
        </View>
      </View>
    )
  );
});

const styles = StyleSheet.create({
  container: {
    gap: 24,
  },
  section: {
    gap: 8,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  pastSectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

export default OpenHouseList;
